import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Loader2, Minus, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SYSTEMS = ["Brand new setup", "Cisco PBX", "Other PBX", "Not sure"];

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    users: 10, sites: 1, current_system: SYSTEMS[0], message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); if (error) setError(""); };
  const step = (k, d, min = 1) => {
    if (error) setError("");
    setForm((f) => ({ ...f, [k]: Math.max(min, (parseInt(f[k], 10) || min) + d) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please add your name and email so we can send the quote.");
      return;
    }
    const users = parseInt(form.users, 10);
    const sites = parseInt(form.sites, 10);
    if (!users || users < 1 || !sites || sites < 1) {
      setError("Please enter a valid number of users and sites.");
      return;
    }
    setError("");
    setStatus("sending");
    try {
      await axios.post(`${API}/quotes`, { ...form, users, sites });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please email us at contact@quadrimtech.com");
    }
  };

  return (
    <div data-testid="quote-form" className="rounded-2xl border border-white/10 bg-[#0A0A0B]/80 backdrop-blur-sm p-6 md:p-8">
      <AnimatePresence mode="wait">
        {status === "done" ? (
          <motion.div key="ok" data-testid="quote-form-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-8 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-accent text-black mb-6">
              <Check className="h-8 w-8" />
            </div>
            <h3 className="font-heading text-3xl font-medium tracking-tight mb-3">Thank you!</h3>
            <p className="text-[#A1A1AA] max-w-sm mx-auto">
              Thank you for your enquiry — one of our engineers will get back to
              you within the next 2 days.
            </p>
          </motion.div>
        ) : (
          <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={submit} noValidate className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name *">
                <input data-testid="quote-name-input" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="lead-input" />
              </Field>
              <Field label="Email *">
                <input data-testid="quote-email-input" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className="lead-input" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Company">
                <input data-testid="quote-company-input" value={form.company} onChange={set("company")} placeholder="Company name" className="lead-input" />
              </Field>
              <Field label="Phone">
                <input data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} placeholder="+1 ..." className="lead-input" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Stepper label="Number of users" testid="quote-users" value={form.users} onDec={() => step("users", -1)} onInc={() => step("users", 1)} onChange={set("users")} />
              <Stepper label="Number of sites" testid="quote-sites" value={form.sites} onDec={() => step("sites", -1)} onInc={() => step("sites", 1)} onChange={set("sites")} />
            </div>

            <div>
              <span className="block font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">Current phone system</span>
              <div className="flex flex-wrap gap-2">
                {SYSTEMS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`quote-system-${s.split(" ")[0].toLowerCase()}`}
                    onClick={() => { setForm((f) => ({ ...f, current_system: s })); if (error) setError(""); }}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${
                      form.current_system === s ? "border-accent bg-accent text-black" : "border-white/15 text-[#A1A1AA] hover:border-white/40"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <Field label="Anything else?">
              <textarea data-testid="quote-message-input" value={form.message} onChange={set("message")} rows={3} placeholder="Timeline, must-have features, existing numbers to port..." className="lead-input resize-none" />
            </Field>

            {error && <p data-testid="quote-form-error" className="text-sm text-red-400">{error}</p>}

            <button
              data-testid="quote-form-submit"
              type="submit"
              disabled={status === "sending"}
              className="group w-full flex items-center justify-center gap-3 rounded-full bg-accent text-black px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:bg-white transition-colors duration-300 disabled:opacity-60"
            >
              {status === "sending" ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Sending</>
              ) : (
                <>Request my quote <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" /></>
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">{label}</span>
      {children}
    </label>
  );
}

function Stepper({ label, value, onDec, onInc, onChange, testid }) {
  return (
    <div>
      <span className="block font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">{label}</span>
      <div className="flex items-stretch">
        <button type="button" data-testid={`${testid}-dec`} onClick={onDec} className="grid w-11 place-items-center rounded-l-[10px] border border-white/12 border-r-0 bg-[#0a0a0b] text-[#A1A1AA] hover:text-white hover:border-accent transition-colors duration-200" aria-label={`Decrease ${label}`}>
          <Minus className="h-4 w-4" />
        </button>
        <input
          data-testid={`${testid}-input`}
          type="number"
          min="1"
          value={value}
          onChange={onChange}
          className="lead-input rounded-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button type="button" data-testid={`${testid}-inc`} onClick={onInc} className="grid w-11 place-items-center rounded-r-[10px] border border-white/12 border-l-0 bg-[#0a0a0b] text-[#A1A1AA] hover:text-white hover:border-accent transition-colors duration-200" aria-label={`Increase ${label}`}>
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
